import React, { Component, ErrorInfo } from 'react';
import { AlertTriangle, RefreshCw, ChevronLeft } from 'lucide-react';
import { Button } from '../common/Button';
import { errorService } from '../../services/error';
import { analytics } from '../../utils/analytics';

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to error service
    errorService.handleError(error, {
      componentStack: errorInfo.componentStack,
      ...this.getErrorContext(),
    });

    // Track error in analytics
    analytics.trackEvent({
      category: 'Error',
      action: 'react_error_boundary',
      label: error.message,
      metadata: {
        name: error.name,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
      },
    });
  }

  private getErrorContext() {
    return {
      url: window.location.href,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      screenSize: `${window.innerWidth}x${window.innerHeight}`,
      viewportSize: `${document.documentElement.clientWidth}x${document.documentElement.clientHeight}`,
    };
  }

  private handleReload = () => {
    analytics.trackEvent({
      category: 'Error',
      action: 'reload_from_error',
      label: this.state.error?.message,
    });
    window.location.reload();
  };

  private handleBack = () => {
    analytics.trackEvent({
      category: 'Error',
      action: 'navigate_back_from_error',
      label: this.state.error?.message,
    });
    window.history.back();
  };

  private handleReportError = () => {
    const errorDetails = {
      error: this.state.error,
      errorInfo: this.state.errorInfo,
      context: this.getErrorContext(),
    };

    // In a real app, this would send to your error reporting service
    console.error('Error Report:', errorDetails);

    analytics.trackEvent({
      category: 'Error',
      action: 'report_error',
      label: this.state.error?.message,
      metadata: errorDetails,
    });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-lg w-full text-center">
            <AlertTriangle className="w-16 h-16 text-error mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              عذراً، حدث خطأ ما
            </h1>
            <p className="text-gray-600 mb-6">
              نعتذر عن هذا الخطأ. يرجى المحاولة مرة أخرى أو الاتصال بفريق الدعم إذا استمرت المشكلة.
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="mb-6 text-left rtl:text-right">
                <div className="bg-error bg-opacity-10 rounded-lg p-4">
                  <p className="text-error font-mono text-sm">
                    {this.state.error.message}
                  </p>
                  {this.state.error.stack && (
                    <pre className="mt-2 text-xs text-gray-600 overflow-x-auto">
                      {this.state.error.stack}
                    </pre>
                  )}
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 rtl:space-x-reverse">
              <Button
                onClick={this.handleReload}
                className="w-full sm:w-auto flex items-center justify-center"
              >
                <RefreshCw className="w-4 h-4 ml-2 rtl:mr-2" />
                إعادة تحميل الصفحة
              </Button>
              
              <Button
                variant="secondary"
                onClick={this.handleBack}
                className="w-full sm:w-auto flex items-center justify-center"
              >
                <ChevronLeft className="w-4 h-4 ml-2 rtl:mr-2" />
                العودة للصفحة السابقة
              </Button>

              {process.env.NODE_ENV === 'development' && (
                <Button
                  variant="error"
                  onClick={this.handleReportError}
                  className="w-full sm:w-auto"
                >
                  الإبلاغ عن الخطأ
                </Button>
              )}
            </div>

            <div className="mt-6 text-sm text-gray-500">
              رقم المرجع: {Date.now().toString(36)}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}